import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { Auth } from 'aws-amplify';
import { Form, Formik } from 'formik';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import OnboardingClient from 'src/clients/OnboardingClient';
import { AppViewMode } from 'src/constants/uiConsts';
import { PortalConfigContext } from 'src/context';
import history from 'src/history';
import { useAnonymousId } from 'src/hooks/useAnonymousId';
import { useIsAssemblyBrandEnabled } from 'src/hooks/useIsAssemblyBrandEnabled';
import { useAppSelector } from 'src/hooks/useStore';
import { AboutYourCompany } from 'src/routes/onboarding/setup/components/about-your-company/AboutYourCompany';
import { CompanySetup } from 'src/routes/onboarding/setup/components/CompanySetup';
import { CreateAccount } from 'src/routes/onboarding/setup/components/CreateAccount';
import { CustomizeYourWorkspace } from 'src/routes/onboarding/setup/components/customize-your-workspace/CustomizeYourWorkspace';
import { useRandomizedCustomizationOptions } from 'src/routes/onboarding/setup/components/customize-your-workspace/utils';
import { LegacyOnboardingPageLayout } from 'src/routes/onboarding/setup/components/LegacyOnboardingPageLayout';
import { LoginChallenge } from 'src/routes/onboarding/setup/components/LoginChallenge';
import { OnboardingPageLayout } from 'src/routes/onboarding/setup/components/OnboardingPageLayout';
import { WhoYouServe } from 'src/routes/onboarding/setup/components/who-you-serve/WhoYouServe';
import {
  FormPageInfo,
  FormPageInfoProvider,
} from 'src/routes/onboarding/setup/context/formPageInfoContext';
import { useOnboardingQueryParams } from 'src/routes/onboarding/setup/hooks/useOnboardingQueryParams';
import {
  OnboardingFormValues,
  OnboardingStep,
  TrackedEventsType,
} from 'src/routes/onboarding/setup/types';
import { TrackEvent } from 'src/utils/AnalyticsUtils';
import { CognitoUserWithAttributes } from 'src/utils/CognitoUtils';

const useOnboardingStep = () => {
  const { prefillEmail, step } = useOnboardingQueryParams();

  const [currentStep, setCurrentStep] = useState<OnboardingStep>(() => {
    if (step === 'company-setup' && prefillEmail !== undefined) {
      return step;
    }
    return 'create-account';
  });

  // on the final step set the query param to "stage=complete_onboarding" for tracking
  const url = new URL(window.location.href);
  useEffect(() => {
    if (currentStep === 'customize-your-workspace') {
      url.searchParams.set('stage', 'complete_onboarding');
      window.history.pushState({}, '', url);
    } else if (url.searchParams.has('stage')) {
      url.searchParams.delete('stage');
      window.history.pushState({}, '', url);
    }
  }, [currentStep]);

  const toPreviousStep = useCallback(
    (onNavigate?: (prevStep: OnboardingStep) => void) => {
      let newStep: OnboardingStep;
      switch (currentStep) {
        case 'create-account':
        case 'login-challenge':
        case 'company-setup':
          newStep = 'create-account';
          break;
        case 'about-your-company':
          newStep = 'company-setup';
          break;
        case 'who-you-serve':
          newStep = 'about-your-company';
          break;
        case 'customize-your-workspace':
          newStep = 'who-you-serve';
          break;
      }
      if (onNavigate !== undefined) {
        onNavigate(newStep);
      }
      setCurrentStep(newStep);
    },
    [currentStep],
  );

  const toNextStep = useCallback((context?: { challengeName?: string }) => {
    setCurrentStep((newStep) => {
      switch (newStep) {
        case 'create-account':
          // Check if there's a challenge that requires the login-challenge step
          if (context?.challengeName === 'SOFTWARE_TOKEN_MFA') {
            return 'login-challenge';
          }
          return 'company-setup';
        case 'login-challenge':
          return 'company-setup';
        case 'company-setup':
          return 'about-your-company';
        case 'about-your-company':
          return 'who-you-serve';
        case 'who-you-serve':
        case 'customize-your-workspace':
          return 'customize-your-workspace';
      }
    });
  }, []);

  const progressPercentTable: Record<OnboardingStep, number> = {
    'create-account': 0,
    'login-challenge': 0,
    'company-setup': 20,
    'about-your-company': 40,
    'who-you-serve': 60,
    'customize-your-workspace': 80,
  };

  return {
    currentStep,
    setCurrentStep,
    toPreviousStep,
    toNextStep,
    progressPercent: progressPercentTable[currentStep],
  } as const;
};

const useGoogleAuth = ({
  onAuthenticate,
}: {
  onAuthenticate: (user: CognitoUserWithAttributes) => void;
}) => {
  const { authSource } = useOnboardingQueryParams();

  // if the user is already logged in with google call the authenticate handler
  const isLoggedInWithGoogle =
    authSource === CognitoHostedUIIdentityProvider.Google;
  const [fetchingAuthenticatedUser, setFetchingAuthenticatedUser] =
    useState(isLoggedInWithGoogle);
  useEffect(() => {
    if (isLoggedInWithGoogle) {
      setFetchingAuthenticatedUser(true);
      Auth.currentAuthenticatedUser()
        .then((user: CognitoUserWithAttributes) => {
          // if the user is available, then we can run the authenticate handler
          onAuthenticate(user);
          setFetchingAuthenticatedUser(false);
        })
        .catch((e) => {
          console.error('error signing in the user', e);
          // todo show this error in ui
        });
    }
  }, [isLoggedInWithGoogle]);

  return fetchingAuthenticatedUser;
};

const identifyUser = (values: OnboardingFormValues) => {
  // set the email in global variable to enable enhanced
  // conversions for google ads in Google Tag Manager
  window.createAccountData_email = values.email;
  if (analytics && analytics.identify) {
    // identify onboarding user to have traits from onboarding flow
    const { password: _password, ...trackableTraits } = values;
    analytics.identify(values.email, trackableTraits, {
      integrations: {
        Amplitude: false, // disable amplitude identify during onboarding to avoid email being sent to Amplitude as id
      },
    });
  }
};

export const OnboardingSetupPage = () => {
  const portalConfig = useContext(PortalConfigContext);
  const savedAnonymousId = useAnonymousId();
  const { referred, source, prefillEmail, name, workspaceTemplateId } =
    useOnboardingQueryParams();

  // Block /signup route on client portals - only allow on internal dashboard
  useEffect(() => {
    if (portalConfig.viewMode === AppViewMode.CLIENT) {
      history.push('/login?step=signUp');
      return;
    }
    // On internal dashboard, allow /signup to work normally (no redirect)
  }, [portalConfig.viewMode]);

  const {
    currentStep,
    setCurrentStep,
    toPreviousStep,
    toNextStep,
    progressPercent,
  } = useOnboardingStep();

  /**
   * when there is a saved anonymousId, we need use that for analytics.
   * this is needed to be able to connect the event the user took on
   * the marketing site to the event on the portal.
   */
  React.useEffect(() => {
    if (analytics?.user && savedAnonymousId) {
      analytics.user().anonymousId(savedAnonymousId);
    }
    TrackEvent('Onboarding - Started');
  }, [savedAnonymousId]);

  // to prevent multi submission for a given analytics event,
  // we define a state that persists the events submission state.
  const [trackedEvents, setTrackedEvents] = React.useState<TrackedEventsType>(
    {},
  );
  const handleAnalyticsEventTracked = useCallback(
    (eventStep: OnboardingStep) => {
      setTrackedEvents((prev) => ({
        ...prev,
        [eventStep]: true,
      }));
    },
    [],
  );

  /**
   * This state is used to store the validation schema and submit handler for the current onboarding step.
   * It is passed down to each onboarding step component through the FormPageInfoProvider to allow them
   * to define their own form behavior while still sharing the same Formik component instance.
   *
   * This approach is based off of Formik's own multi step wizard example
   * https://github.com/jaredpalmer/formik/blob/main/examples/MultistepWizard.js
   */
  const [formPageInfo, setFormPageInfo] = useState<
    FormPageInfo<OnboardingFormValues>
  >({});

  const randomizedCustomizationOptions = useRandomizedCustomizationOptions();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAssemblyBrandEnabled } = useIsAssemblyBrandEnabled();

  const [cognitoUser, setCognitoUser] = useState<
    CognitoUserWithAttributes | undefined
  >(undefined);

  return (
    <Formik<OnboardingFormValues>
      initialValues={{
        referred: referred ?? '',
        source: source ?? '',
        workspaceTemplateId: workspaceTemplateId || '',
        email: prefillEmail ?? '',
        password: '',
        // get user name if it is present in the query param (e.g. after user log in with reusable email,
        // login page sets the users name in the query param)
        name: name ?? '',
        phone: '',
        foundBy: '',
        foundByCustom: '',
        companyName: '',
        portalUrl: '',
        industry: '',
        industryLabel: '',
        companySize: '',
        companySizeLabel: '',
        signoutRedirectURL: '',
        anonymousId: '',
        clientType: '',
        clientTypeLabel: '',
        clientCount: '',
        clientCountLabel: '',
        companyAge: '',
        companyAgeLabel: '',
        clientSource: '',
        clientSourceLabel: '',
        companyRevenue: '',
        companyRevenueLabel: '',
        priorities: [],
        prioritiesLabels: '',
        enabledFeatures: [],
        isAssemblyBrandEnabled,
        challengeResponse: '',
        brandData: null,
      }}
      onSubmit={async (values, formikHelpers) => {
        const valuesStaged = { ...values };
        if (values.anonymousId === '') {
          const anonymousId =
            analytics.user && analytics.user() ? savedAnonymousId : '';
          valuesStaged.anonymousId = anonymousId;
          formikHelpers.setFieldValue('anonymousId', anonymousId);
        }

        identifyUser(valuesStaged);

        try {
          const stepSubmitHandler = formPageInfo.submitHandler;
          if (stepSubmitHandler !== undefined) {
            const { navigationContext } =
              (await stepSubmitHandler({
                values: valuesStaged,
                formikHelpers,
                setIsSubmitting,
                identifyUser,
              })) ?? {};

            formikHelpers.setTouched({});
            toNextStep(navigationContext);

            // Fire brand customization prefetch after the create-account step
            // (or after company-setup when the user came from the existing-email
            // flow or Google SSO, since both skip the create-account submission).
            // The ~11s API call overlaps with the user filling subsequent steps.
            // When it resolves, the result is stored in Formik values.
            if (
              currentStep === 'create-account' ||
              (currentStep === 'company-setup' &&
                (prefillEmail !== undefined ||
                  valuesStaged.loginSource ===
                    CognitoHostedUIIdentityProvider.Google))
            ) {
              const fetchBrand = async () => {
                try {
                  const brandData =
                    await OnboardingClient.FetchBrandCustomization(
                      valuesStaged.email,
                    );
                  formikHelpers.setFieldValue('brandData', brandData);
                } catch {
                  // Brand customization is best-effort; swallow errors.
                }
              };
              void fetchBrand();
            }
          }
        } catch (e) {
          console.error(e);
        }
      }}
      validationSchema={formPageInfo.validationSchema}
    >
      {({ values, setValues, resetForm, initialValues }) => {
        const googleSignInProgress = useAppSelector(
          (state) => state.auth.processingCallback,
        );
        const fetchingAuthenticatedUser = useGoogleAuth({
          // when the user is authenticated with google
          // set the form values to reflect the authed user
          // and set the first step to company-setup
          onAuthenticate: (user) => {
            setValues({
              ...values,
              name: `${user.attributes.given_name} ${user.attributes.family_name}`,
              email: user.attributes.email,
              loginSource: CognitoHostedUIIdentityProvider.Google,
              picture: user.attributes.picture,
            });
            setCurrentStep('company-setup');
          },
        });
        const isLoading = googleSignInProgress || fetchingAuthenticatedUser;

        return (
          <Form className="h-full">
            <FormPageInfoProvider<OnboardingFormValues>
              formPageInfo={formPageInfo}
              setFormPageInfo={setFormPageInfo}
            >
              {currentStep === 'create-account' ||
              currentStep === 'login-challenge' ||
              currentStep === 'company-setup' ? (
                <LegacyOnboardingPageLayout
                  isFirstStep={currentStep === 'create-account'}
                  isLoading={isLoading}
                  onClickBack={() => {
                    toPreviousStep((prevStep) => {
                      // reset form state except for email and password whenever navigating back to the create account step
                      if (prevStep === 'create-account') {
                        resetForm({
                          values: {
                            ...initialValues,
                            email: values.email,
                            password: values.password,
                          },
                        });
                      }
                    });
                  }}
                >
                  {currentStep === 'create-account' ? (
                    <CreateAccount
                      trackedEvents={trackedEvents}
                      onAnalyticsEventTracked={handleAnalyticsEventTracked}
                      onAuthenticateUser={(user) => setCognitoUser(user)}
                    />
                  ) : currentStep === 'login-challenge' ? (
                    <LoginChallenge
                      trackedEvents={trackedEvents}
                      onAnalyticsEventTracked={handleAnalyticsEventTracked}
                      authenticatedUser={cognitoUser}
                    />
                  ) : (
                    <CompanySetup
                      trackedEvents={trackedEvents}
                      onAnalyticsEventTracked={handleAnalyticsEventTracked}
                    />
                  )}
                </LegacyOnboardingPageLayout>
              ) : (
                <OnboardingPageLayout
                  onClickBack={() => toPreviousStep()}
                  progressPercent={progressPercent}
                  isLoading={isLoading || isSubmitting}
                >
                  {currentStep === 'about-your-company' ? (
                    <AboutYourCompany
                      trackedEvents={trackedEvents}
                      onAnalyticsEventTracked={handleAnalyticsEventTracked}
                    />
                  ) : currentStep === 'who-you-serve' ? (
                    <WhoYouServe
                      trackedEvents={trackedEvents}
                      onAnalyticsEventTracked={handleAnalyticsEventTracked}
                    />
                  ) : (
                    <CustomizeYourWorkspace
                      {...randomizedCustomizationOptions}
                    />
                  )}
                </OnboardingPageLayout>
              )}
            </FormPageInfoProvider>
          </Form>
        );
      }}
    </Formik>
  );
};
